import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { PermissionOverrides, Permissions } from "@kickwin/shared";
import { api, type AdminUser, type Bot, type RoleTemplate, type WebhookStatus } from "@/lib/api";
import { useCan, useMe } from "@/lib/shell-context";
import { exceptionCount, PermissionMatrix } from "@/components/permission-matrix";
import { useConfirm } from "@/components/confirm";
import { CopySettingsDialog } from "@/components/copy-settings";
import { MoreHorizontal } from "lucide-react";
import { Button as UiButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Mono,
  Panel,
  Select,
  Status,
  Table,
  Td,
  Th,
} from "@/components/primitives";

/**
 * Bots and admins (spec §9, §12). Super admin only — adding a bot creates a
 * whole tenant, with its own group, players and data.
 */

export function BotsPage() {
  const canManage = useCan("bots", "manage");
  // Copying settings is the owner's alone (decision 55).
  const isOwner = useMe().role === "super_admin";
  const [copyTo, setCopyTo] = useState<Bot | null>(null);
  const queryClient = useQueryClient();
  const [token, setToken] = useState("");

  const bots = useQuery({ queryKey: ["bots"], queryFn: () => api.get<Bot[]>("/bots") });

  const add = useMutation({
    mutationFn: () => api.post<Bot>("/bots", { token: token.trim() }),
    onSuccess: () => {
      setToken("");
      void queryClient.invalidateQueries({ queryKey: ["bots"] });
    },
  });

  const disable = useMutation({
    mutationFn: (id: number) => api.del(`/bots/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["bots"] }),
  });

  // Disabling deletes the webhook, so coming back is not just a status flip:
  // the worker re-registers it with a new secret from the stored token.
  const enable = useMutation({
    mutationFn: (id: number) => api.post<Bot>(`/bots/${id}/enable`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bots"] });
      void queryClient.invalidateQueries({ queryKey: ["bot-webhook"] });
    },
  });

  // Rotating re-registers the webhook with a fresh secret, so the same token
  // pasted again also repairs a bot whose webhook points somewhere wrong.
  const [rotateId, setRotateId] = useState("");
  const [rotateToken, setRotateToken] = useState("");
  const rotate = useMutation({
    mutationFn: () =>
      api.post<Bot>(`/bots/${rotateId || bots.data?.[0]?.id}/rotate-token`, {
        token: rotateToken.trim(),
      }),
    onSuccess: () => {
      setRotateToken("");
      void queryClient.invalidateQueries({ queryKey: ["bots"] });
      void queryClient.invalidateQueries({ queryKey: ["bot-webhook"] });
    },
  });

  return (
    <div className="grid grid-cols-1 gap-4">
      {canManage && (
        <Panel title="Add a bot">
          <form
            className="grid items-end gap-3 px-5 py-3 sm:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <Field label="Bot token from BotFather">
              <Input
                className="mono"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="123456789:AA…"
                required
              />
            </Field>
            <p className="-mt-1 text-muted-foreground sm:order-last sm:col-span-full">
              Stored encrypted and never shown again. The webhook is set up for you.
            </p>
            <Button type="submit" disabled={add.isPending}>
              {add.isPending ? "Checking with Telegram" : "Add bot"}
            </Button>
          </form>
          {add.error && (
            <div className="px-5 pb-3">
              <ErrorNote error={add.error} />
            </div>
          )}
        </Panel>
      )}

      <Panel title="Bots">
        {bots.isLoading ? (
          <Loading />
        ) : bots.error ? (
          <div className="p-3.5">
            <ErrorNote error={bots.error} />
          </div>
        ) : bots.data && bots.data.length === 0 ? (
          <Empty
            title="No bots yet"
            hint="Paste a token above. Everything else — players, sources, the CS group — belongs to a bot."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Bot</Th>
                <Th className="w-40">Telegram ID</Th>
                <Th className="w-28">Status</Th>
                <Th className="w-32">Added</Th>
                <Th className="w-48" />
              </tr>
            </thead>
            <tbody>
              {bots.data?.map((bot) => (
                <tr key={bot.id}>
                  <Td primary>
                    <Link
                      to={`/bots/${bot.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      @{bot.username}
                    </Link>
                    {bot.title && (
                      <span className="text-muted-foreground md:ml-2">{bot.title}</span>
                    )}
                  </Td>
                  <Td label="Telegram ID">
                    <Mono>{bot.telegramBotId}</Mono>
                  </Td>
                  <Td label="Status">
                    <Status value={bot.status} />
                  </Td>
                  <Td label="Added" className="text-muted-foreground">
                    {new Date(bot.createdAt).toLocaleDateString()}
                  </Td>
                  <Td>
                    <span className="flex flex-wrap items-center justify-end gap-1">
                      {isOwner && (bots.data?.length ?? 0) > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => setCopyTo(bot)}>
                          Copy settings
                        </Button>
                      )}
                      {canManage &&
                        (bot.status === "active" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => disable.mutate(bot.id)}
                            disabled={disable.isPending}
                          >
                            Disable
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => enable.mutate(bot.id)}
                            disabled={enable.isPending}
                          >
                            {enable.isPending ? "Reconnecting" : "Enable"}
                          </Button>
                        ))}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
          Disabling stops the webhook and keeps every message. Nothing is deleted, and Enable
          reconnects the bot with its stored token.
        </p>
        {enable.error && (
          <div className="border-t border-border px-5 py-2.5">
            <ErrorNote error={enable.error} />
          </div>
        )}
      </Panel>

      {copyTo && bots.data && (
        <CopySettingsDialog target={copyTo} bots={bots.data} onClose={() => setCopyTo(null)} />
      )}

      {bots.data && bots.data.length > 0 && (
        <Panel title="Webhooks">
          <ul className="divide-y divide-border/60">
            {bots.data.map((bot) => (
              <WebhookRow key={bot.id} bot={bot} />
            ))}
          </ul>
          <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
            Asked of Telegram directly. A bot at the wrong address receives nothing: paste its
            current token in “Replace a bot's token” below to reconnect it.
          </p>
        </Panel>
      )}

      {canManage && bots.data && bots.data.length > 0 && (
        <Panel title="Replace a bot's token">
          <form
            className="grid items-end gap-3 px-5 py-3 md:grid-cols-[auto_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              rotate.mutate();
            }}
          >
            <Field label="Bot">
              <Select
                className="w-full"
                value={rotateId || String(bots.data[0]!.id)}
                onChange={(e) => setRotateId(e.target.value)}
              >
                {bots.data.map((bot) => (
                  <option key={bot.id} value={bot.id}>
                    @{bot.username}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="New token from BotFather">
              <Input
                className="mono"
                type="password"
                autoComplete="off"
                value={rotateToken}
                onChange={(e) => setRotateToken(e.target.value)}
                placeholder="123456789:AA…"
                required
              />
            </Field>
            <p className="-mt-1 text-muted-foreground md:order-last md:col-span-full">
              After revoking a leaked token in BotFather. Pasting the current token again reconnects
              the webhook, which fixes a bot that has stopped receiving messages.
            </p>
            <Button type="submit" disabled={rotate.isPending}>
              {rotate.isPending ? "Checking with Telegram" : "Replace token"}
            </Button>
          </form>
          {rotate.error && (
            <div className="px-5 pb-3">
              <ErrorNote error={rotate.error} />
            </div>
          )}
          {rotate.isSuccess && (
            <p className="border-t border-border px-5 py-2.5 text-ok">
              Token replaced and webhook reconnected.
            </p>
          )}
        </Panel>
      )}
    </div>
  );
}

/** One bot's live webhook, compared with the address it should have. */
function WebhookRow({ bot }: { bot: Bot }) {
  const hook = useQuery({
    queryKey: ["bot-webhook", bot.id],
    queryFn: () => api.get<WebhookStatus>(`/bots/${bot.id}/webhook`),
    enabled: bot.status === "active",
    refetchInterval: 60_000,
  });
  const data = hook.data;

  const [tone, label] =
    bot.status !== "active"
      ? (["text-muted-foreground", "Disabled"] as const)
      : !data
        ? (["text-muted-foreground", hook.error ? "Could not ask Telegram" : "Checking"] as const)
        : !data.available
          ? (["text-muted-foreground", "Unavailable"] as const)
          : !data.matches
            ? (["text-destructive", data.url ? "Wrong address" : "Not set"] as const)
            : data.lastErrorMessage && data.pendingUpdates > 0
              ? (["text-amber-600 dark:text-amber-400", "Delivery errors"] as const)
              : (["text-ok", "Connected"] as const);

  return (
    <li className="grid grid-cols-1 gap-1.5 px-5 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">@{bot.username}</span>
        <span className={`inline-flex items-center gap-1.5 ${tone}`}>
          <span className="size-1.5 rounded-full bg-current" aria-hidden />
          {label}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={bot.status !== "active" || hook.isFetching}
          onClick={() => void hook.refetch()}
        >
          {hook.isFetching ? "Checking" : "Check again"}
        </Button>
      </div>
      {data?.available && (
        <dl className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)]">
          <dt className="text-muted-foreground">Current URL</dt>
          <dd>
            <Mono className="break-all">{data.url || "none"}</Mono>
          </dd>
          {!data.matches && (
            <>
              <dt className="text-muted-foreground">Should be</dt>
              <dd>
                <Mono className="break-all">{data.expectedUrl}</Mono>
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">Waiting updates</dt>
          <dd>
            <Mono>{data.pendingUpdates}</Mono>
          </dd>
          {data.lastErrorMessage && (
            <>
              <dt className="text-muted-foreground">Last error</dt>
              <dd className="[overflow-wrap:anywhere]">
                {data.lastErrorMessage}
                {data.lastErrorAt && (
                  <span className="ml-2 text-muted-foreground">
                    {new Date(data.lastErrorAt).toLocaleString()}
                  </span>
                )}
              </dd>
            </>
          )}
        </dl>
      )}
      {hook.error && <ErrorNote error={hook.error} />}
    </li>
  );
}

export function AdminsPage() {
  const me = useMe();
  const canManage = useCan("admins", "manage");
  const isOwner = me.role === "super_admin";
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [superAdmin, setSuperAdmin] = useState(false);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [botIds, setBotIds] = useState<number[]>([]);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const admins = useQuery({ queryKey: ["admins"], queryFn: () => api.get<AdminUser[]>("/admins") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api.get<RoleTemplate[]>("/roles") });
  const bots = useQuery({ queryKey: ["bots"], queryFn: () => api.get<Bot[]>("/bots") });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admins"] });
    void queryClient.invalidateQueries({ queryKey: ["roles"] });
  };

  const chosenRole = roleId ?? roles.data?.find((r) => r.name === "Viewer")?.id ?? null;

  /** Shown once, then gone: the server keeps only a hash (decision 48, §7). */
  const [handover, setHandover] = useState<{ email: string; password: string } | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api.post<AdminUser & { temporaryPassword: string }>("/admins", {
        email: email.trim(),
        superAdmin,
        roleId: superAdmin ? null : chosenRole,
        botIds: superAdmin ? [] : botIds,
      }),
    onSuccess: (created) => {
      setHandover({ email: created.email, password: created.temporaryPassword });
      setEmail("");
      setBotIds([]);
      setSuperAdmin(false);
      invalidate();
    },
  });

  const resetPassword = useMutation({
    mutationFn: (admin: AdminUser) =>
      api.post<{ temporaryPassword: string }>(`/admins/${admin.id}/reset-password`),
    onSuccess: (result, admin) =>
      setHandover({ email: admin.email, password: result.temporaryPassword }),
  });

  const clearTwoFactor = useMutation({
    mutationFn: (admin: AdminUser) => api.post(`/admins/${admin.id}/clear-two-factor`),
  });

  const confirm = useConfirm();
  const signOut = useMutation({
    mutationFn: (admin: AdminUser) => api.post(`/admins/${admin.id}/sign-out`),
  });

  const toggleActive = useMutation({
    mutationFn: (admin: AdminUser) =>
      api.patch(`/admins/${admin.id}`, { isActive: !admin.isActive }),
    onSuccess: invalidate,
  });

  return (
    <div className="grid grid-cols-1 gap-4">
      {canManage && (
        <Panel
          title="Add an admin"
          description="You will get a temporary password to hand over — shown once."
        >
          <form
            className="grid gap-4 px-5 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <div className="grid gap-3 md:grid-cols-[1fr_280px]">
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Field>
              <Field label="Role">
                <Select
                  className="w-full"
                  value={superAdmin ? "" : (chosenRole ?? "")}
                  disabled={superAdmin}
                  onChange={(e) => setRoleId(Number(e.target.value))}
                >
                  {roles.data?.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {isOwner && (
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={superAdmin}
                  onChange={(e) => setSuperAdmin(e.target.checked)}
                />
                <span>
                  Super admin
                  <span className="block text-muted-foreground">
                    Owner-level: every bot, every permission, beyond any role.
                  </span>
                </span>
              </label>
            )}
            {!superAdmin && (bots.data?.length ?? 0) > 0 && (
              <BotPicker bots={bots.data ?? []} value={botIds} onChange={setBotIds} />
            )}
            <div>
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? "Adding" : "Add admin"}
              </Button>
            </div>
            {add.error && <ErrorNote error={add.error} />}
          </form>
        </Panel>
      )}

      <Panel title="Admins">
        {admins.isLoading ? (
          <Loading />
        ) : admins.error ? (
          <div className="p-5">
            <ErrorNote error={admins.error} />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th className="w-48">Role</Th>
                <Th className="w-24">Bots</Th>
                <Th className="w-32 md:max-xl:hidden">Last seen</Th>
                <Th className="w-44" />
              </tr>
            </thead>
            <tbody>
              {admins.data?.map((admin) => {
                const exceptions = exceptionCount(admin.overrides);
                const editable =
                  canManage && (isOwner || (admin.role !== "super_admin" && admin.id !== me.id));
                return (
                  <tr key={admin.id} className={admin.isActive ? "" : "opacity-60"}>
                    <Td primary className="break-all">
                      {admin.email}
                      {admin.id === me.id && <span className="text-muted-foreground"> (you)</span>}
                    </Td>
                    <Td label="Role">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {admin.role === "super_admin" ? (
                          <Status value="active" label="Super admin" />
                        ) : (
                          <span className="font-medium">{admin.roleName ?? "No role"}</span>
                        )}
                        {exceptions > 0 && (
                          <span className="rounded-full bg-accent px-1.5 py-0.5 text-[11px] font-medium text-accent-foreground">
                            +{exceptions} {exceptions === 1 ? "exception" : "exceptions"}
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td label="Bots" className="text-muted-foreground">
                      {admin.role === "super_admin" ? "All" : admin.botIds.length}
                    </Td>
                    <Td label="Last seen" className="text-muted-foreground md:max-xl:hidden">
                      {admin.lastSeenAt ? new Date(admin.lastSeenAt).toLocaleDateString() : "Never"}
                    </Td>
                    <Td>
                      {editable && (
                        <span className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="quiet" onClick={() => setEditing(admin)}>
                            Edit
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <UiButton
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`More for ${admin.email}`}
                              >
                                <MoreHorizontal />
                              </UiButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={async () => {
                                  if (
                                    await confirm({
                                      title: `Sign ${admin.email} out everywhere?`,
                                      description:
                                        "Every sign-in they have now stops working. They can sign in again; deactivate them to keep them out.",
                                      confirmLabel: "Sign out everywhere",
                                      destructive: true,
                                    })
                                  )
                                    signOut.mutate(admin);
                                }}
                              >
                                Sign out everywhere
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={async () => {
                                  if (
                                    await confirm({
                                      title: `Reset ${admin.email}'s password?`,
                                      description:
                                        "You get a new temporary password to hand over, shown once. Their current password and every session they have stop working.",
                                      confirmLabel: "Reset password",
                                      destructive: true,
                                    })
                                  )
                                    resetPassword.mutate(admin);
                                }}
                              >
                                Reset password
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={async () => {
                                  if (
                                    await confirm({
                                      title: `Clear ${admin.email}'s two-factor?`,
                                      description:
                                        "For a lost phone, once their backup codes are gone too. They enrol again on their next sign-in.",
                                      confirmLabel: "Clear two-factor",
                                      destructive: true,
                                    })
                                  )
                                    clearTwoFactor.mutate(admin);
                                }}
                              >
                                Clear two-factor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant={admin.isActive ? "destructive" : "default"}
                                onSelect={() => toggleActive.mutate(admin)}
                              >
                                {admin.isActive ? "Deactivate" : "Reactivate"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {(toggleActive.error || signOut.error || resetPassword.error || clearTwoFactor.error) && (
          <div className="p-5">
            <ErrorNote
              error={
                toggleActive.error ?? signOut.error ?? resetPassword.error ?? clearTwoFactor.error
              }
            />
          </div>
        )}
        {clearTwoFactor.isSuccess && (
          <p className="border-t border-border px-5 py-3 text-ok">
            Two-factor cleared. They will set it up again next time they sign in.
          </p>
        )}
        {signOut.isSuccess && (
          <p className="border-t border-border px-5 py-3 text-ok">
            Signed out. They will be asked to sign in again on their next action.
          </p>
        )}
        <p className="border-t border-border px-5 py-3 text-muted-foreground">
          Changes take effect on their next request. CS agents never need an account here.
        </p>
      </Panel>

      {handover && (
        <HandoverDialog
          email={handover.email}
          password={handover.password}
          onClose={() => setHandover(null)}
        />
      )}

      {editing && (
        <EditAdmin
          admin={editing}
          roles={roles.data ?? []}
          bots={bots.data ?? []}
          isOwner={isOwner}
          limit={isOwner ? null : me.permissions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function BotPicker({
  bots,
  value,
  onChange,
}: {
  bots: Bot[];
  value: number[];
  onChange: (next: number[]) => void;
}) {
  return (
    <fieldset className="grid gap-1.5">
      <legend className="mb-1 font-medium">Bots they can open</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {bots.map((bot) => (
          <label key={bot.id} className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={value.includes(bot.id)}
              onChange={(e) =>
                onChange(
                  e.target.checked ? [...value, bot.id] : value.filter((id) => id !== bot.id),
                )
              }
            />
            @{bot.username}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** One admin's role, bots and personal exceptions (decision 42). */
function EditAdmin({
  admin,
  roles,
  bots,
  isOwner,
  limit,
  onClose,
  onSaved,
}: {
  admin: AdminUser;
  roles: RoleTemplate[];
  bots: Bot[];
  isOwner: boolean;
  limit: Permissions | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [superAdmin, setSuperAdmin] = useState(admin.role === "super_admin");
  const [roleId, setRoleId] = useState<number | null>(admin.roleId ?? roles[0]?.id ?? null);
  const [botIds, setBotIds] = useState<number[]>(admin.botIds);
  const [overrides, setOverrides] = useState<PermissionOverrides>(admin.overrides ?? {});
  const base = roles.find((r) => r.id === roleId)?.permissions ?? null;

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admins/${admin.id}`, {
        ...(isOwner ? { superAdmin } : {}),
        ...(superAdmin
          ? {}
          : {
              roleId,
              botIds,
              overrides: exceptionCount(overrides) > 0 ? overrides : null,
            }),
      }),
    onSuccess: onSaved,
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="break-all">{admin.email}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-5">
          {isOwner && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={superAdmin}
                onChange={(e) => setSuperAdmin(e.target.checked)}
              />
              Super admin — every bot and every permission
            </label>
          )}
          {!superAdmin && (
            <>
              <Field label="Role">
                <Select
                  className="w-full md:max-w-xs"
                  value={roleId ?? ""}
                  onChange={(e) => setRoleId(Number(e.target.value))}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {bots.length > 0 && <BotPicker bots={bots} value={botIds} onChange={setBotIds} />}
              {base && (
                <div className="grid gap-2">
                  <div>
                    <div className="font-medium">Exceptions for this person</div>
                    <div className="text-muted-foreground">
                      Leave “Same as role” unless this person needs something different.
                    </div>
                  </div>
                  <PermissionMatrix
                    kind="overrides"
                    base={base}
                    value={overrides}
                    onChange={setOverrides}
                    limit={limit}
                  />
                </div>
              )}
            </>
          )}
          {save.error && <ErrorNote error={save.error} />}
        </div>
        <DialogFooter>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The one moment a password is visible (decision 48, §7).
 *
 * The server keeps only a hash, so closing this is the end of it — there is
 * no "show it again". Deliberately not emailed, not logged, and not written
 * to the audit trail, which records only that a password was issued.
 */
function HandoverDialog({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hand this over</DialogTitle>
          <DialogDescription>
            Shown once. Give it to {email} directly — it is not sent anywhere.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <code className="block rounded-md border border-border bg-secondary p-3 text-center font-mono text-sm tracking-wide break-all">
            {password}
          </code>
          <Button
            type="button"
            variant="quiet"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(password);
                setCopied(true);
              } catch {
                // Clipboard access can be refused; the password is on screen
                // either way, so this is a convenience, not the mechanism.
                setCopied(false);
              }
            }}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
          <p className="text-xs text-muted-foreground">
            They will be asked to choose their own password and set up two-factor before the panel
            opens for them.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
